import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import type { ShimmerProps, ShimmerVariant } from './Shimmer.types';

const gradient = keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(25%)' },
});

const pulse = keyframes({
  '0%': { opacity: 1 },
  '50%': { opacity: 0.4 },
  '100%': { opacity: 1 },
});

type RootProps = {
  $width?: ShimmerProps['width'];
  $height?: ShimmerProps['height'];
  hasChildren: boolean;
} & Required<Pick<ShimmerProps, 'variant' | 'animation'>>;

const Root = styled.div<RootProps>(({ theme, $width, $height, variant, animation, hasChildren }) => {
  const borderRadiusMap: Record<ShimmerVariant, string | number> = {
    text: theme.eclipseTier1.radius['8px'],
    rectangular: 0,
    rounded: theme.eclipseTier1.radius['8px'],
    circular: '50%',
  };

  return {
    backgroundColor: theme.eclipseTier2Color.skeleton.default,
    overflow: 'hidden',
    position: 'relative',
    borderRadius: borderRadiusMap[variant],
    width: $width,
    height: $height ?? (hasChildren ? 'auto' : '1em'),
    ...(hasChildren && {
      '& > *': { visibility: 'hidden' },
      ...($width == null && { maxWidth: 'fit-content' }),
    }),

    ...(animation === 'wave' && {
      ':after': {
        position: 'absolute',
        content: '""',
        inset: 0,
        transform: 'translateX(-100%)',
        width: '400%',
        background: `linear-gradient(to right, ${theme.eclipseTier2Color.skeleton.default}, ${theme.eclipseTier2Color.skeleton.shimmer}, ${theme.eclipseTier2Color.skeleton.default})`,
        animation: `${gradient} linear 2s infinite`,
      },
    }),

    ...(animation === 'pulse' && {
      animation: `${pulse} 2s ease-in-out 0.5s infinite`,
    }),
  };
});

export const S = { Root } as const;
